import { useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Map, Satellite } from 'lucide-react'
import type { Device } from '@/types/device.types'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

// Fix for default marker icon in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

interface DeviceMapProps {
  device: Device
}

export const DeviceMap = ({ device }: DeviceMapProps) => {
  const [mapType, setMapType] = useState<'street' | 'satellite'>('street')

  if (!device.lat || !device.long) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Device Location</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[400px] text-muted-foreground">
            No location data available for this device
          </div>
        </CardContent>
      </Card>
    )
  }

  const position: [number, number] = [Number(device.lat), Number(device.long)]

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle>Device Location</CardTitle>
            <p className="text-sm text-muted-foreground">
              {device.name} - {Number(device.lat).toFixed(6)}, {Number(device.long).toFixed(6)}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={mapType === 'street' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMapType('street')}
            >
              <Map className="h-4 w-4 mr-2" />
              Street
            </Button>
            <Button
              variant={mapType === 'satellite' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMapType('satellite')}
            >
              <Satellite className="h-4 w-4 mr-2" />
              Satellite
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[500px] rounded-lg overflow-hidden border">
          <MapContainer
            center={position}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
          >
            {mapType === 'street' ? (
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            ) : (
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            )}
            <Marker position={position}>
              <Popup>
                <div className="p-2">
                  <h3 className="font-semibold">{device.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {device.serialNumber}
                  </p>
                  <p className="text-sm">Type: {device.deviceType}</p>
                  <p className="text-sm">Status: {device.status}</p>
                  {device.batteryLevel && (
                    <p className="text-sm">Battery: {device.batteryLevel}%</p>
                  )}
                  <a
                    href={`https://www.google.com/maps?q=${device.lat},${device.long}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline text-sm mt-2 inline-block"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </div>
      </CardContent>
    </Card>
  )
}
